import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Switch,
  FormControlLabel,
  Divider,
  Paper,
  Grid,
  IconButton,
  Collapse,
  CircularProgress
} from '@mui/material';
import {
  Add,
  Edit,
  Save,
  Lock,
  LockOpen,
  ExpandMore,
  ExpandLess,
  PlayArrow,
  Check,
  AutoAwesome,
  OpenInNew
} from '@mui/icons-material';
import LoadingButton from '@mui/lab/LoadingButton';
import { useTheme } from '@mui/material/styles';
import {
  BackendAlertRule,
  AlertRuleFormData,
  RuleTemplate,
  RulePreviewResult,
  ThresholdOperator,
  AlertSeverity
} from '../types';
import { ruleService } from '../../../../../api/cansense/ruleApi';
import { generateAIResponse } from '../../../../../api/openai/openaiAPI';

// ============================================================================
// Types
// ============================================================================

interface AlertRuleDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: AlertRuleFormData) => Promise<boolean>;
  onBatchSave?: (data: AlertRuleFormData[]) => Promise<boolean>;
  mode: 'create' | 'edit' | 'duplicate';
  rule?: BackendAlertRule | null;
  exporterType?: string | null;
}

type CreationMode = 'template' | 'custom';

// ============================================================================
// Constants
// ============================================================================

const DURATION_OPTIONS = [
  { value: '1m', label: '1 minute' },
  { value: '2m', label: '2 minutes' },
  { value: '5m', label: '5 minutes' },
  { value: '10m', label: '10 minutes' },
  { value: '15m', label: '15 minutes' },
  { value: '30m', label: '30 minutes' }
];

const OPERATOR_OPTIONS: { value: ThresholdOperator; label: string }[] = [
  { value: '>', label: '> (greater than)' },
  { value: '<', label: '< (less than)' },
  { value: '>=', label: '>= (greater or equal)' },
  { value: '<=', label: '<= (less or equal)' },
  { value: '==', label: '== (equal)' },
  { value: '!=', label: '!= (not equal)' }
];

const SEVERITIES: { value: AlertSeverity; label: string; color: string }[] = [
  { value: 'critical', label: 'Critical', color: '#d32f2f' },
  { value: 'warning', label: 'Warning', color: '#ed6c02' },
  { value: 'info', label: 'Info', color: '#0288d1' }
];

// ============================================================================
// Helpers
// ============================================================================

/**
 * Convert a display name to a snake_case alert_name.
 * "High CPU Usage" -> "custom_high_cpu_usage"
 */
const toAlertName = (name: string): string => {
  const snake = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s_]/g, '')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
  return snake ? `custom_${snake}` : '';
};

/**
 * Validate an alert_name: must start with letter or underscore, then alphanumeric/underscore.
 */
const isValidAlertName = (name: string): boolean => {
  return /^[a-zA-Z_][a-zA-Z0-9_]*$/.test(name);
};

// ============================================================================
// Component
// ============================================================================

export const AlertRuleDialog: React.FC<AlertRuleDialogProps> = ({
  open,
  onClose,
  onSave,
  onBatchSave,
  mode,
  rule = null,
  exporterType = null
}) => {
  const theme = useTheme();
  const isEdit = mode === 'edit';
  const isDuplicate = mode === 'duplicate';

  // Creation mode (create only)
  const [creationMode, setCreationMode] = useState<CreationMode>('template');

  // Template state
  const [templates, setTemplates] = useState<RuleTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedTemplates, setSelectedTemplates] = useState<RuleTemplate[]>([]);

  // Form state
  const [name, setName] = useState('');
  const [alertName, setAlertName] = useState('');
  const [alertNameLocked, setAlertNameLocked] = useState(true);
  const [severity, setSeverity] = useState<AlertSeverity>('warning');
  const [forDuration, setForDuration] = useState('5m');
  const [thresholdOperator, setThresholdOperator] = useState<ThresholdOperator>('>');
  const [thresholdValue, setThresholdValue] = useState<string>('');
  const [expr, setExpr] = useState('');
  const [summaryTemplate, setSummaryTemplate] = useState('');
  const [descriptionTemplate, setDescriptionTemplate] = useState('');
  const [enabled, setEnabled] = useState(true);

  // UI state
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  // Preview state
  const [previewing, setPreviewing] = useState(false);
  const [previewResult, setPreviewResult] = useState<RulePreviewResult | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  // CANai generation state
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);

  // ========================================================================
  // Template loading
  // ========================================================================

  useEffect(() => {
    if (open && !isEdit && !isDuplicate) {
      loadTemplates();
    }
  }, [open, isEdit, isDuplicate, exporterType]);

  const loadTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const result = await ruleService.getTemplates(
        exporterType ? { exporter_type: exporterType } : undefined
      );
      setTemplates(result);
    } catch (err) {
      console.error('Failed to load templates:', err);
      setTemplates([]);
    } finally {
      setLoadingTemplates(false);
    }
  };

  // ========================================================================
  // Populate form in edit mode
  // ========================================================================

  useEffect(() => {
    if ((isEdit || isDuplicate) && rule) {
      const dupName = isDuplicate ? `${rule.name} (Copy)` : (rule.name || '');
      setName(dupName);
      setAlertName(isDuplicate ? toAlertName(dupName) : (rule.alert_name || ''));
      setSeverity((rule.severity as AlertSeverity) || 'warning');
      setForDuration(rule.for_duration || '5m');
      setExpr(rule.expr || '');
      setThresholdOperator((rule.threshold_operator as ThresholdOperator) || '>');
      setThresholdValue(rule.threshold_value !== null ? String(rule.threshold_value) : '');
      setSummaryTemplate(rule.summary_template || '');
      setDescriptionTemplate(rule.description_template || '');
      setEnabled(rule.enabled ?? true);
      setAlertNameLocked(isDuplicate);
      setCreationMode(rule.threshold_value !== null ? 'template' : 'custom');
      setHasChanges(false);
      // Open advanced if annotations exist
      if (rule.summary_template || rule.description_template) {
        setAdvancedOpen(true);
      }
    }
  }, [isEdit, isDuplicate, rule]);

  // ========================================================================
  // Auto-generate alert_name from name (create mode, locked)
  // ========================================================================

  useEffect(() => {
    if (!isEdit && alertNameLocked && name) {
      setAlertName(toAlertName(name));
    }
  }, [name, alertNameLocked, isEdit]);

  // ========================================================================
  // Track changes in edit mode
  // ========================================================================

  useEffect(() => {
    if (isEdit && rule) {
      const changed =
        name !== (rule.name || '') ||
        alertName !== (rule.alert_name || '') ||
        severity !== ((rule.severity as AlertSeverity) || 'warning') ||
        forDuration !== (rule.for_duration || '5m') ||
        expr !== (rule.expr || '') ||
        thresholdOperator !== ((rule.threshold_operator as ThresholdOperator) || '>') ||
        thresholdValue !== (rule.threshold_value !== null ? String(rule.threshold_value) : '') ||
        summaryTemplate !== (rule.summary_template || '') ||
        descriptionTemplate !== (rule.description_template || '') ||
        enabled !== (rule.enabled ?? true);
      setHasChanges(changed);
    }
  }, [name, alertName, severity, forDuration, expr, thresholdOperator, thresholdValue, summaryTemplate, descriptionTemplate, enabled, isEdit, rule]);

  // ========================================================================
  // Template selection
  // ========================================================================

  const handleToggleTemplate = (template: RuleTemplate) => {
    setSelectedTemplates(prev => {
      const exists = prev.find(t => t.id === template.id);
      if (exists) {
        return prev.filter(t => t.id !== template.id);
      }
      return [...prev, template];
    });
    setError('');
    setPreviewResult(null);
  };

  // When exactly one template is selected, populate form fields
  useEffect(() => {
    if (selectedTemplates.length === 1) {
      const template = selectedTemplates[0];
      const variant = exporterType
        ? template.variants.find(v => v.exporter_type === exporterType) || template.variants[0]
        : template.variants[0];

      setName(template.name);
      setSeverity((template.default_severity as AlertSeverity) || 'warning');
      setForDuration(template.default_for_duration || '5m');

      if (variant) {
        setExpr(variant.expr_template || '');
        setSummaryTemplate(variant.summary_template || '');
        setDescriptionTemplate(variant.description_template || '');
      }

      if (template.default_threshold !== null) {
        setThresholdValue(String(template.default_threshold));
      }
      setThresholdOperator('>');
    }
  }, [selectedTemplates, exporterType]);

  // ========================================================================
  // Preview
  // ========================================================================

  const handlePreview = async () => {
    const effectiveExpr = getEffectiveExpr();
    if (!effectiveExpr) {
      setError('Please provide a PromQL expression');
      return;
    }

    setPreviewing(true);
    setPreviewResult(null);
    setError('');

    try {
      // Step 1: Validate PromQL syntax
      const validation = await ruleService.validatePromQL(effectiveExpr);
      if (!validation.valid) {
        setPreviewResult({
          valid: false,
          error: validation.error || 'Invalid PromQL expression'
        });
        setPreviewOpen(true);
        return;
      }

      // Step 2: Preview (run against Prometheus)
      const threshold = thresholdValue ? parseFloat(thresholdValue) : undefined;
      const result = await ruleService.previewRule(effectiveExpr, threshold);
      setPreviewResult({
        valid: true,
        result: result.series?.map((s: { labels: Record<string, string>; value: string | number }) => ({
          metric: s.labels,
          value: [0, String(s.value)]
        })) || result.result?.map((r: { metric: Record<string, string>; value: [number, string] }) => r) || [],
        would_fire: result.would_fire ?? (result.matches > 0)
      });
      setPreviewOpen(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Preview failed';
      setPreviewResult({
        valid: false,
        error: message
      });
      setPreviewOpen(true);
    } finally {
      setPreviewing(false);
    }
  };

  // ========================================================================
  // CANai PromQL generation
  // ========================================================================

  const handleAiGenerate = async () => {
    if (!aiPrompt.trim()) return;

    setAiGenerating(true);
    setError('');

    try {
      // Fetch available metrics from Prometheus so the AI uses real metric names
      let metricsContext = '';
      try {
        const metricsData = await ruleService.getMetrics(undefined, 500);
        const metrics = Array.isArray(metricsData) ? metricsData : metricsData?.metrics || [];
        if (metrics.length > 0) {
          metricsContext = `\n\nAvailable metrics in this Prometheus instance (use ONLY these metric names):\n${metrics.join(', ')}`;
        }
      } catch {
        // If metrics fetch fails, proceed without them
      }

      const response = await generateAIResponse(
        aiPrompt.trim(),
        `You are a Prometheus monitoring expert. Generate a valid PromQL expression based on the user's description.

Rules:
- Return ONLY the raw PromQL expression, nothing else - no explanation, no markdown, no backticks
- Use {{threshold}} as a placeholder for the threshold value where appropriate
- The expression should be suitable for a Prometheus alerting rule
- If the user describes a threshold condition, include the comparison operator and {{threshold}} placeholder
- ONLY use metric names from the available metrics list below. Do NOT invent metric names.${metricsContext}`,
        'gpt-4o',
        0.3,
        200
      );

      if (response.success && response.data?.response) {
        let generatedExpr = response.data.response.trim();
        // Strip markdown code fences if present
        generatedExpr = generatedExpr.replace(/^```(?:promql)?\s*\n?/i, '').replace(/\n?```\s*$/i, '').trim();
        setExpr(generatedExpr);
        setPreviewResult(null);
        setAiOpen(false);
        setAiPrompt('');
      }
    } catch (err) {
      setError('CANai generation failed. Please try again or write the expression manually.');
    } finally {
      setAiGenerating(false);
    }
  };

  // ========================================================================
  // Expression helpers
  // ========================================================================

  const getEffectiveExpr = (): string => {
    // Always substitute {{threshold}} before sending to Prometheus
    if (expr && thresholdValue) {
      return expr.replace(/\{\{threshold\}\}/g, thresholdValue);
    }
    // If expression has {{threshold}} but no value, substitute with 0 for validation
    if (expr && expr.includes('{{threshold}}')) {
      return expr.replace(/\{\{threshold\}\}/g, '0');
    }
    return expr;
  };

  const getPreviewExpr = (): string => {
    if (!expr) return '';
    if (thresholdValue) {
      return expr.replace(/\{\{threshold\}\}/g, thresholdValue);
    }
    return expr;
  };

  // ========================================================================
  // Form handlers
  // ========================================================================

  const handleClose = () => {
    // Reset all form state
    setCreationMode('template');
    setSelectedTemplates([]);
    setTemplates([]);
    setName('');
    setAlertName('');
    setAlertNameLocked(true);
    setSeverity('warning');
    setForDuration('5m');
    setThresholdOperator('>');
    setThresholdValue('');
    setExpr('');
    setSummaryTemplate('');
    setDescriptionTemplate('');
    setEnabled(true);
    setSubmitting(false);
    setError('');
    setHasChanges(false);
    setAdvancedOpen(false);
    setPreviewing(false);
    setPreviewResult(null);
    setPreviewOpen(false);
    setAiPrompt('');
    setAiGenerating(false);
    setAiOpen(false);
    onClose();
  };

  /**
   * Build form data for a single template using its defaults
   */
  const buildTemplateFormData = (template: RuleTemplate): AlertRuleFormData => {
    const variant = exporterType
      ? template.variants.find(v => v.exporter_type === exporterType) || template.variants[0]
      : template.variants[0];

    let templateExpr = variant?.expr_template || '';
    if (template.default_threshold !== null && templateExpr.includes('{{threshold}}')) {
      templateExpr = templateExpr.replace(/\{\{threshold\}\}/g, String(template.default_threshold));
    }

    return {
      name: template.name,
      alert_name: toAlertName(template.name),
      expr: templateExpr,
      for_duration: template.default_for_duration || '5m',
      severity: (template.default_severity as AlertSeverity) || 'warning',
      threshold_value: template.default_threshold,
      threshold_operator: template.default_threshold !== null ? '>' : null,
      summary_template: variant?.summary_template || null,
      description_template: variant?.description_template || null,
      exporter_type: exporterType || null,
      enabled: true
    };
  };

  const handleBatchSubmit = async () => {
    if (selectedTemplates.length === 0) return;

    setSubmitting(true);
    setError('');

    try {
      const batchData = selectedTemplates.map(buildTemplateFormData);

      if (onBatchSave) {
        const success = await onBatchSave(batchData);
        if (success) {
          handleClose();
        } else {
          setError('Failed to create some alert rules');
        }
      } else {
        // Fallback: create one at a time
        let allSuccess = true;
        for (const data of batchData) {
          const success = await onSave(data);
          if (!success) allSuccess = false;
        }
        if (allSuccess) {
          handleClose();
        } else {
          setError('Some rules failed to create');
        }
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create alert rules';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    // Validation
    if (!name.trim()) {
      setError('Rule name is required');
      return;
    }
    if (!alertName.trim() || !isValidAlertName(alertName)) {
      setError('Alert name is required and must contain only letters, numbers, and underscores (starting with a letter or underscore)');
      return;
    }

    const effectiveExpr = getEffectiveExpr();
    if (!effectiveExpr) {
      setError('PromQL expression is required');
      return;
    }

    const needsThreshold = expr.includes('{{threshold}}');
    if (needsThreshold && !thresholdValue) {
      setError('Threshold value is required');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const formData: AlertRuleFormData = {
        name: name.trim(),
        alert_name: alertName.trim(),
        expr: effectiveExpr,
        for_duration: forDuration,
        severity,
        threshold_value: thresholdValue ? parseFloat(thresholdValue) : null,
        threshold_operator: thresholdValue ? thresholdOperator : null,
        summary_template: summaryTemplate || null,
        description_template: descriptionTemplate || null,
        exporter_type: exporterType || null,
        enabled
      };

      const success = await onSave(formData);
      if (success) {
        handleClose();
      } else {
        setError(`Failed to ${isEdit ? 'update' : 'create'} alert rule`);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : `Failed to ${isEdit ? 'update' : 'create'} alert rule`;
      setError(message);
      console.error(`${isEdit ? 'Update' : 'Create'} rule failed:`, err);
    } finally {
      setSubmitting(false);
    }
  };

  // ========================================================================
  // Validation
  // ========================================================================

  const isFormValid = (): boolean => {
    // Batch mode — just need templates selected
    if (creationMode === 'template' && selectedTemplates.length > 1) return true;

    if (!name.trim()) return false;
    if (!alertName.trim() || !isValidAlertName(alertName)) return false;

    if (creationMode === 'template' || ((isEdit || isDuplicate) && rule?.threshold_value !== null)) {
      if (expr.includes('{{threshold}}') && !thresholdValue) return false;
      if (!expr) return false;
    } else {
      if (!expr.trim()) return false;
    }

    if (isEdit && !hasChanges) return false;
    return true;
  };

  // ========================================================================
  // Render: Template selector
  // ========================================================================

  const renderTemplateSelector = () => {
    if (isEdit || isDuplicate || creationMode !== 'template') return null;

    return (
      <>
        <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
          Select a Template
        </Typography>

        {loadingTemplates ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : templates.length === 0 ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            No templates available. Switch to custom mode to write a rule from scratch.
          </Alert>
        ) : (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[...templates]
              .sort((a, b) => {
                if (exporterType) {
                  const aMatch = a.variants.some(v => v.exporter_type === exporterType);
                  const bMatch = b.variants.some(v => v.exporter_type === exporterType);
                  if (aMatch && !bMatch) return -1;
                  if (!aMatch && bMatch) return 1;
                }
                return a.name.localeCompare(b.name);
              })
              .map((template) => {
                const isSelected = selectedTemplates.some(t => t.id === template.id);
                const matchesExporter = exporterType
                  ? template.variants.some(v => v.exporter_type === exporterType)
                  : false;

                return (
                  <Grid item xs={12} sm={6} key={template.id}>
                    <Paper
                      variant={isSelected ? 'elevation' : 'outlined'}
                      elevation={isSelected ? 3 : 0}
                      onClick={() => handleToggleTemplate(template)}
                      sx={{
                        p: 2,
                        cursor: 'pointer',
                        border: isSelected
                          ? `2px solid ${theme.palette.primary.main}`
                          : matchesExporter
                            ? `1px solid ${theme.palette.primary.light}`
                            : '1px solid #e0e0e0',
                        bgcolor: isSelected
                          ? `${theme.palette.primary.main}10`
                          : 'transparent',
                        transition: 'all 0.2s',
                        '&:hover': {
                          borderColor: theme.palette.primary.main,
                          bgcolor: `${theme.palette.primary.main}05`
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {template.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                            {template.description}
                          </Typography>
                          <Chip
                            label={template.default_severity}
                            size="small"
                            sx={{
                              bgcolor: SEVERITIES.find(s => s.value === template.default_severity)?.color + '20',
                              color: SEVERITIES.find(s => s.value === template.default_severity)?.color,
                              border: `1px solid ${SEVERITIES.find(s => s.value === template.default_severity)?.color || '#999'}`,
                              fontSize: '0.7rem'
                            }}
                          />
                        </Box>
                        {isSelected && (
                          <Check sx={{ color: theme.palette.primary.main, ml: 1 }} />
                        )}
                      </Box>
                    </Paper>
                  </Grid>
                );
              })}
          </Grid>
        )}

        {/* Selection summary for multiselect */}
        {selectedTemplates.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
            <Typography variant="caption" color="text.secondary">
              {selectedTemplates.length} selected:
            </Typography>
            {selectedTemplates.map(t => (
              <Chip
                key={t.id}
                label={t.name}
                size="small"
                onDelete={() => handleToggleTemplate(t)}
                color="primary"
                variant="outlined"
              />
            ))}
          </Box>
        )}

        <Divider sx={{ my: 3 }} />
      </>
    );
  };

  // ========================================================================
  // Render: Expression section
  // ========================================================================

  const renderExpressionSection = () => {
    const isTemplateExpr = creationMode === 'template' || (isEdit && rule?.threshold_value !== null);
    const hasThresholdPlaceholder = expr.includes('{{threshold}}');

    return (
      <>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
            Expression
          </Typography>
          <Typography
            variant="caption"
            component="a"
            href="https://prometheus.io/docs/prometheus/latest/querying/basics/"
            target="_blank"
            rel="noopener noreferrer"
            sx={{ color: 'primary.main', display: 'flex', alignItems: 'center', gap: 0.5, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          >
            PromQL docs <OpenInNew sx={{ fontSize: 14 }} />
          </Typography>
        </Box>

        {isTemplateExpr ? (
          <>
            {hasThresholdPlaceholder && (
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <FormControl sx={{ flex: 1 }}>
                <InputLabel>Operator</InputLabel>
                <Select
                  value={thresholdOperator}
                  onChange={(e: SelectChangeEvent) => {
                    setThresholdOperator(e.target.value as ThresholdOperator);
                    setPreviewResult(null);
                  }}
                  label="Operator"
                >
                  {OPERATOR_OPTIONS.map((op) => (
                    <MenuItem key={op.value} value={op.value}>
                      {op.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label={`Threshold${selectedTemplate?.threshold_unit ? ` (${selectedTemplate.threshold_unit})` : ''}`}
                type="number"
                value={thresholdValue}
                onChange={(e) => {
                  setThresholdValue(e.target.value);
                  setPreviewResult(null);
                }}
                sx={{ flex: 1 }}
                    inputProps={{
                  min: selectedTemplate?.threshold_min ?? undefined,
                  max: selectedTemplate?.threshold_max ?? undefined,
                  step: selectedTemplate?.threshold_step ?? undefined
                }}
              />
            </Box>
            )}

            {/* PromQL preview */}
            {expr && (
              <Box
                sx={{
                  p: 2,
                  bgcolor: theme.palette.grey[900],
                  borderRadius: 1,
                  mb: 2,
                  fontFamily: 'monospace',
                  fontSize: '0.85rem',
                  color: theme.palette.common.white,
                  overflowX: 'auto',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all'
                }}
              >
                {getPreviewExpr()}
              </Box>
            )}
          </>
        ) : (
          <TextField
            fullWidth
            multiline
            minRows={3}
            maxRows={8}
            label="PromQL Expression *"
            placeholder="e.g., 100 - (avg by(instance) (irate(node_cpu_seconds_total{mode='idle'}[5m])) * 100) > 80"
            value={expr}
            onChange={(e) => {
              setExpr(e.target.value);
              setPreviewResult(null);
            }}
            sx={{
              mb: 1,
              '& .MuiInputBase-input': {
                fontFamily: 'monospace',
                fontSize: '0.85rem'
              }
            }}
          />
        )}

        {/* CANai generation — available in all modes */}
        <Box sx={{ mb: 2 }}>
          <Button
            size="small"
            variant="outlined"
            color="secondary"
            startIcon={<AutoAwesome />}
            onClick={() => setAiOpen(!aiOpen)}
            sx={{ textTransform: 'none', mb: aiOpen ? 1 : 0 }}
          >
            {aiOpen ? 'Hide CANai' : 'Generate with CANai'}
          </Button>
          <Collapse in={aiOpen}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Describe what you want to monitor, e.g. 'Alert when disk usage exceeds 90% on Linux servers'"
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey && aiPrompt.trim()) {
                    e.preventDefault();
                    handleAiGenerate();
                  }
                }}
                disabled={aiGenerating}
                multiline
                minRows={1}
                maxRows={3}
              />
              <LoadingButton
                variant="contained"
                size="small"
                onClick={handleAiGenerate}
                loading={aiGenerating}
                disabled={!aiPrompt.trim()}
                sx={{ minWidth: 100, whiteSpace: 'nowrap' }}
              >
                Generate
              </LoadingButton>
            </Box>
          </Collapse>
        </Box>

        {/* Preview button */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <LoadingButton
            size="small"
            variant="outlined"
            onClick={handlePreview}
            loading={previewing}
            startIcon={<PlayArrow />}
            disabled={!expr}
          >
            Preview Rule
          </LoadingButton>
        </Box>

        {/* Preview results */}
        <Collapse in={previewOpen && !!previewResult}>
          {previewResult && (
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                mb: 2,
                bgcolor: previewResult.valid
                  ? theme.palette.success.light + '10'
                  : theme.palette.error.light + '10'
              }}
            >
              {previewResult.valid ? (
                <>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: previewResult.would_fire ? 'success.main' : 'warning.main' }}>
                    {previewResult.would_fire
                      ? `Would fire - ${previewResult.result?.length || 0} matching series`
                      : 'Valid expression, but no matching series found. The metric may not exist or the condition is not currently met.'}
                  </Typography>
                  {previewResult.result && previewResult.result.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {previewResult.result.slice(0, 10).map((r, i) => (
                        <Chip
                          key={i}
                          label={r.metric?.instance || r.metric?.job || `series-${i + 1}`}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                      {previewResult.result.length > 10 && (
                        <Chip
                          label={`+${previewResult.result.length - 10} more`}
                          size="small"
                          color="default"
                        />
                      )}
                    </Box>
                  )}
                </>
              ) : (
                <Alert severity="error" sx={{ m: 0 }}>
                  {previewResult.error || 'Expression validation failed'}
                </Alert>
              )}
            </Paper>
          )}
        </Collapse>
      </>
    );
  };

  // ========================================================================
  // Main render
  // ========================================================================

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { minHeight: '500px' } }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {isEdit ? <Edit sx={{ mr: 1 }} /> : <Add sx={{ mr: 1 }} />}
          {isEdit ? 'Edit Alert Rule' : isDuplicate ? 'Duplicate Alert Rule' : 'Create Alert Rule'}
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {/* ================================================================ */}
          {/* Creation Mode Toggle (create only, not duplicate) */}
          {/* ================================================================ */}
          {!isEdit && !isDuplicate && (
            <>
              <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                <Chip
                  label="From Template"
                  onClick={() => {
                    setCreationMode('template');
                    setPreviewResult(null);
                  }}
                  variant={creationMode === 'template' ? 'filled' : 'outlined'}
                  color={creationMode === 'template' ? 'primary' : 'default'}
                />
                <Chip
                  label="Custom"
                  onClick={() => {
                    setCreationMode('custom');
                    setSelectedTemplate(null);
                    setPreviewResult(null);
                  }}
                  variant={creationMode === 'custom' ? 'filled' : 'outlined'}
                  color={creationMode === 'custom' ? 'primary' : 'default'}
                />
              </Box>

              <Divider sx={{ mb: 3 }} />
            </>
          )}

          {/* ================================================================ */}
          {/* Template Selector (create + template mode) */}
          {/* ================================================================ */}
          {renderTemplateSelector()}

          {/* ================================================================ */}
          {/* Rule Basics — hidden when multiple templates selected */}
          {/* ================================================================ */}
          {(creationMode !== 'template' || selectedTemplates.length <= 1) && (
          <>
          <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
            Rule Details
          </Typography>

          <TextField
            fullWidth
            label="Rule Name *"
            placeholder="e.g., High CPU Usage"
            value={name}
            onChange={(e) => setName(e.target.value)}
            sx={{ mb: 2 }}
          />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
            <TextField
              fullWidth
              label="Alert Name *"
              placeholder="e.g., custom_high_cpu_usage"
              value={alertName}
              onChange={(e) => {
                if (!alertNameLocked || isEdit) {
                  setAlertName(e.target.value);
                }
              }}
              disabled={!isEdit && alertNameLocked}
              helperText={
                alertNameLocked && !isEdit
                  ? 'Auto-generated from name'
                  : 'Must start with a letter or underscore, then letters/numbers/underscores'
              }
              error={!!alertName && !isValidAlertName(alertName)}
              sx={{
                '& .MuiInputBase-input': {
                  fontFamily: 'monospace',
                  fontSize: '0.9rem'
                }
              }}
            />
            {!isEdit && (
              <IconButton
                onClick={() => setAlertNameLocked(!alertNameLocked)}
                size="small"
                title={alertNameLocked ? 'Unlock to edit manually' : 'Lock to auto-generate'}
              >
                {alertNameLocked ? <Lock fontSize="small" /> : <LockOpen fontSize="small" />}
              </IconButton>
            )}
          </Box>

          {/* Severity chips */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Severity
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {SEVERITIES.map((sev) => (
                <Chip
                  key={sev.value}
                  label={sev.label}
                  onClick={() => setSeverity(sev.value)}
                  variant={severity === sev.value ? 'filled' : 'outlined'}
                  sx={{
                    bgcolor: severity === sev.value ? sev.color + '20' : 'transparent',
                    color: sev.color,
                    border: `1px solid ${sev.color}`,
                    fontWeight: severity === sev.value ? 'bold' : 'normal',
                    '&:hover': {
                      bgcolor: sev.color + '15'
                    }
                  }}
                />
              ))}
            </Box>
          </Box>

          {/* Duration */}
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>For Duration</InputLabel>
            <Select
              value={forDuration}
              onChange={(e: SelectChangeEvent) => setForDuration(e.target.value)}
              label="For Duration"
            >
              {DURATION_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Divider sx={{ my: 3 }} />

          {/* ================================================================ */}
          {/* Expression Section */}
          {/* ================================================================ */}
          {renderExpressionSection()}

          <Divider sx={{ my: 3 }} />

          {/* ================================================================ */}
          {/* Advanced: Annotation Templates */}
          {/* ================================================================ */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              mb: advancedOpen ? 2 : 0
            }}
            onClick={() => setAdvancedOpen(!advancedOpen)}
          >
            <Typography variant="subtitle2" sx={{ color: 'text.secondary', flex: 1 }}>
              Advanced - Annotation Templates
            </Typography>
            {advancedOpen ? <ExpandLess /> : <ExpandMore />}
          </Box>

          <Collapse in={advancedOpen}>
            <TextField
              fullWidth
              label="Summary Template"
              placeholder='e.g., CPU usage is {{ $value }}% on {{ $labels.instance }}'
              value={summaryTemplate}
              onChange={(e) => setSummaryTemplate(e.target.value)}
              helperText="Go template for alert summary. Use {{ $labels.* }} and {{ $value }}"
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Description Template"
              placeholder='e.g., CPU usage on {{ $labels.instance }} has been above {{ $value }}% for more than 5 minutes.'
              value={descriptionTemplate}
              onChange={(e) => setDescriptionTemplate(e.target.value)}
              helperText="Go template for alert description"
            />
          </Collapse>

          </>
          )}

          {/* ================================================================ */}
          {/* Status Toggle (edit mode only) */}
          {/* ================================================================ */}
          {isEdit && (
            <>
              <Divider sx={{ my: 3 }} />
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 2,
                  bgcolor: enabled ? theme.palette.success.light + '20' : theme.palette.grey[100],
                  borderRadius: 1,
                  border: `1px solid ${enabled ? theme.palette.success.light : theme.palette.grey[300]}`
                }}
              >
                <Box>
                  <Typography variant="subtitle2">
                    Rule Status
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {enabled
                      ? 'This alert rule is active and will fire when conditions are met'
                      : 'This alert rule is disabled and will not fire'}
                  </Typography>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={enabled}
                      onChange={(e) => setEnabled(e.target.checked)}
                      color="success"
                    />
                  }
                  label={enabled ? 'Enabled' : 'Disabled'}
                />
              </Box>
            </>
          )}

          {/* Error display */}
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        {creationMode === 'template' && selectedTemplates.length > 1 ? (
          <LoadingButton
            onClick={handleBatchSubmit}
            variant="contained"
            loading={submitting}
            startIcon={<Add />}
          >
            Create {selectedTemplates.length} Rules
          </LoadingButton>
        ) : (
          <LoadingButton
            onClick={handleSubmit}
            variant="contained"
            disabled={!isFormValid()}
            loading={submitting}
            startIcon={isEdit ? <Save /> : <Add />}
          >
            {isEdit ? 'Save Changes' : isDuplicate ? 'Duplicate Rule' : 'Create Rule'}
          </LoadingButton>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AlertRuleDialog;
